/**
 * SA-1185: the client-side sort for a table whose read returns its whole set
 * (Route studies' three lists, Rotations, Contracts). Pure: no store, no DOM.
 * A column's sort value comes from an accessor, never the rendered cell;
 * empties (null, undefined, NaN, blank text) sort last in both directions;
 * ties keep the read's own order; text reads naturally (`LZ-CJ9` before
 * `LZ-CJ10`, case ignored) and a reference by its number. The sort rides a
 * page's own address as `sort` and `dir`; only a key the table declares is
 * read, so a stale link reads as no sort.
 */
import type { RegisterSort, SortDir } from './registerSort'

export type SortValue = string | number | null | undefined
export type SortAccessor<Row> = (row: Row) => SortValue
export interface SortableColumn<Row> {
  sortKey?: string
  sortFirst?: SortDir
  sortValue?: SortAccessor<Row>
}

/** A header's cycle for any key: off → `first` → flipped → off (null). */
export function cycleSort(current: RegisterSort | null, key: string, first: SortDir): RegisterSort | null {
  if (current?.key !== key) return { key, dir: first }
  return current.dir === first ? { key, dir: first === 'asc' ? 'desc' : 'asc' } : null
}

export function isEmptyValue(value: SortValue): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'number') return Number.isNaN(value)
  return value.trim() === ''
}

const COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' })

function compare(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a < b ? -1 : a > b ? 1 : 0
  if (typeof a === 'number') return -1
  if (typeof b === 'number') return 1
  return COLLATOR.compare(a, b)
}

/** The rows ordered by `value` under `sort`, as a new array; the rows as read without either. */
export function sortRows<Row>(rows: readonly Row[], sort: RegisterSort | null, value: SortAccessor<Row> | undefined): Row[] {
  if (!sort || !value) return [...rows]
  const sign = sort.dir === 'asc' ? 1 : -1
  return rows
    .map((row, index) => ({ row, index, v: value(row) }))
    .sort((x, y) => {
      const xEmpty = isEmptyValue(x.v)
      const yEmpty = isEmptyValue(y.v)
      if (xEmpty || yEmpty) return xEmpty === yEmpty ? x.index - y.index : xEmpty ? 1 : -1
      return sign * compare(x.v as string | number, y.v as string | number) || x.index - y.index
    })
    .map((entry) => entry.row)
}

/** `sortRows` with the accessor of the column whose `sortKey` the sort names. */
export function sortByColumn<Row>(rows: readonly Row[], sort: RegisterSort | null, columns: readonly SortableColumn<Row>[]): Row[] {
  const column = sort ? columns.find((c) => c.sortKey === sort.key) : undefined
  return sortRows(rows, sort, column?.sortValue)
}

/** Each sortable column's first click, ascending when it names none: what `readClientSort` takes. */
export function firstsOf(columns: readonly { sortKey?: string; sortFirst?: SortDir }[]): Record<string, SortDir> {
  const out: Record<string, SortDir> = {}
  for (const column of columns) if (column.sortKey) out[column.sortKey] = column.sortFirst ?? 'asc'
  return out
}

/** A reference's trailing number (`RS-9` → 9, `ROT-0142` → 142), else null. */
export function referenceNumber(text: string | null | undefined): number | null {
  const match = /(\d+)\s*$/.exec(text ?? '')
  return match ? Number(match[1]) : null
}

/** An instant as milliseconds, or null when it does not read. */
export function timeOf(iso: string | null | undefined): number | null {
  const time = Date.parse(iso ?? '')
  return Number.isNaN(time) ? null : time
}

/** `sort` and `dir` from an address: a declared key, with `dir` when it reads, else the key's first click. */
export function readClientSort(params: URLSearchParams, firsts: Readonly<Record<string, SortDir>>): RegisterSort | null {
  const key = params.get('sort')
  if (!key || !Object.prototype.hasOwnProperty.call(firsts, key)) return null
  const dir = params.get('dir')
  return { key, dir: dir === 'asc' || dir === 'desc' ? dir : firsts[key] }
}

/** A copy of `params` with `sort` and `dir` set, or both removed for null. */
export function withClientSort(params: URLSearchParams, sort: RegisterSort | null): URLSearchParams {
  const next = new URLSearchParams(params)
  if (sort) {
    next.set('sort', sort.key)
    next.set('dir', sort.dir)
  } else {
    next.delete('sort')
    next.delete('dir')
  }
  return next
}

/** Only `sort` and `dir`: what a list's address keeps while a row of it is open. */
export function sortParams(params: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams()
  for (const key of ['sort', 'dir']) {
    const value = params.get(key)
    if (value !== null) out.set(key, value)
  }
  return out
}

/** A `th`'s `aria-sort` for a sorted direction; nothing on an unsorted column. */
export function ariaSort(dir: SortDir | null | undefined): 'ascending' | 'descending' | undefined {
  return dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined
}
